import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useLocation,
  type RouterHistory,
} from "@tanstack/react-router";
import { createElement } from "react";

import { isApiClientError } from "../api/errors";
import { hasPermission } from "../entities/access/permissions";
import { shellBootstrapQuery } from "./bootstrap";
import { AboutPage } from "./AboutPage";
import { AccountPage } from "./AccountPage";
import { MySubstitutionPage } from "./SubstitutionSection";
import { ErpShell } from "./ErpShell";
import { GuidePage } from "./guide/GuidePage";
import { NotificationsPage } from "./NotificationsPage";
import {
  EmailVerificationPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  SecuritySettingsPage,
  SessionsPage,
} from "./AuthPages";
import { LoginPage } from "./LoginPage";
import { DocumentCreatePage } from "./DocumentCreatePage";
import { CounterpartiesPage } from "./CounterpartiesPage";
import { ApprovalTemplatesPage } from "./ApprovalTemplatesPage";
import { ApprovalTasksPage } from "./ApprovalTasksPage";
import { ApprovalsOverviewPage } from "./ApprovalsOverviewPage";
import { validateCounterpartiesSearch } from "./counterpartiesSupport";
import { MailAdminPage } from "./MailAdminPage";
import { MailboxesPage } from "./MailboxesPage";
import { validateMailboxesSearch } from "./mailboxesSupport";
import { UsersRoute, validateUsersSearch } from "./UserManagementPage";
import { UsersOverviewPage } from "./UsersOverviewPage";
import { UserHierarchyPage } from "./UserHierarchyPage";
import { ScopeManagementPage } from "./ScopeManagementPage";
import { RoleManagementPage } from "./RoleManagementPage";
import { validateLoginSearch } from "./loginSearch";
import {
  documentCreateSearchSchema,
  validateDocumentWorkflowSearch,
} from "./documentWorkflowSearch";
import {
  ModulePlaceholder,
  OverviewPage,
  SiteContentOverviewPage,
  StaticErrorPage,
} from "./pages";
import { AuditLogPage, validateAuditSearch } from "./AuditLogPage";
import { FilesRoute } from "./FilesAdminPage";
import { validateFilesSearch } from "./filesSupport";
import { ModulesAdminPage } from "./ModulesAdminPage";
import { OperationsPage } from "./OperationsPage";
import { SiteNewsPage } from "./SiteNewsPage";
import { SiteSlidesPage } from "./SiteSlidesPage";
import {
  SiteCategoriesPage,
  SitePagesPage,
  SiteSettingsPage,
} from "./SiteContentPages";
import type { ShellServices } from "./services";
import { ErrorPage, LoadingPage, RouteError, type ErrorStatus } from "./states";

interface RouterContext {
  queryClient: QueryClient;
  services: ShellServices;
}

function returnPath(location: {
  pathname: string;
  searchStr: string;
  hash: string;
}): string {
  return `${location.pathname}${location.searchStr}${location.hash}`;
}

async function requirePermission(
  context: RouterContext,
  permission: string,
): Promise<void> {
  const bootstrap = await context.queryClient.ensureQueryData(
    shellBootstrapQuery(context.services),
  );
  if (!hasPermission({ permissions: bootstrap.permissions }, permission)) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirects are control-flow values.
    throw redirect({ to: "/403" });
  }
}

/** Раздел открыт, если у человека есть хотя бы одно из перечисленных прав. */
async function requireAnyPermission(
  context: RouterContext,
  permissions: readonly string[],
): Promise<void> {
  const bootstrap = await context.queryClient.ensureQueryData(
    shellBootstrapQuery(context.services),
  );
  if (
    !permissions.some((permission) =>
      hasPermission({ permissions: bootstrap.permissions }, permission),
    )
  ) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirects are control-flow values.
    throw redirect({ to: "/403" });
  }
}

function guardedPermission(
  context: unknown,
  permission: string,
): Promise<void> {
  return requirePermission(context as RouterContext, permission);
}

export function createApplicationRouter(
  queryClient: QueryClient,
  services: ShellServices,
  history?: RouterHistory,
) {
  const ScopeDetailRoute = () => {
    const scopeId = useLocation({
      select: (location) => location.pathname.split("/").at(-1) ?? "",
    });
    return createElement(ScopeManagementPage, { services, scopeId });
  };
  const DepartmentDetailRoute = () => {
    const parts = useLocation({
      select: (location) => location.pathname.split("/"),
    });
    return createElement(ScopeManagementPage, {
      services,
      scopeId: parts.at(-3),
      departmentId: parts.at(-1),
    });
  };
  const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: Outlet,
    pendingComponent: LoadingPage,
    errorComponent: RouteError,
    notFoundComponent: () => createElement(ErrorPage, { status: 404 }),
  });

  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/login",
    validateSearch: validateLoginSearch,
    component: () => createElement(LoginPage, { services }),
  });

  const unauthorizedRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/401",
    beforeLoad: () => {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirects are control-flow values.
      throw redirect({ to: "/login", search: { redirect: "/" } });
    },
  });

  const errorRoutes = ([403, 404, 409, 422, 429, 503] as const).map((status) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path: `/${status}`,
      component: () =>
        createElement(StaticErrorPage, {
          status: status satisfies ErrorStatus,
        }),
    }),
  );

  const authenticatedRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "authenticated",
    beforeLoad: async ({ context, location }) => {
      try {
        const bootstrap = await context.queryClient.ensureQueryData(
          shellBootstrapQuery(context.services),
        );
        if (
          bootstrap.user.two_factor.setup_required &&
          location.pathname !== "/account/security"
        ) {
          // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirects are control-flow values.
          throw redirect({ to: "/account/security" });
        }
      } catch (error) {
        if (isApiClientError(error) && error.status === 401) {
          // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirects are control-flow values.
          throw redirect({
            to: "/login",
            search: { redirect: returnPath(location) },
          });
        }
        throw error;
      }
    },
    component: () => createElement(ErpShell, { services }),
    pendingComponent: LoadingPage,
    errorComponent: RouteError,
  });

  const overviewRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: "/",
    component: () => createElement(OverviewPage, { services }),
  });
  const moduleRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: "$",
    component: () => createElement(ModulePlaceholder, { services }),
  });
  const mailAdminRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: "/modules/document-workflow/documents",
    validateSearch: validateDocumentWorkflowSearch,
    component: () => createElement(MailAdminPage, { services }),
  });
  const counterpartiesRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: "/modules/document-workflow/counterparties",
    validateSearch: validateCounterpartiesSearch,
    beforeLoad: ({ context }) =>
      guardedPermission(context, "document-workflow.view"),
    component: () => createElement(CounterpartiesPage, { services }),
  });
  const mailboxesRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: "/administration/mailboxes",
    validateSearch: validateMailboxesSearch,
    beforeLoad: ({ context }) =>
      requireAnyPermission(context as RouterContext, [
        "document-workflow.mailboxes.manage",
        "document-workflow.failures.manage",
      ]),
    component: () => createElement(MailboxesPage, { services }),
  });
  const notificationsRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: "/notifications",
    beforeLoad: ({ context }) =>
      guardedPermission(context, "notifications.view"),
    component: () =>
      createElement(NotificationsPage, {
        services: services as Parameters<
          typeof NotificationsPage
        >[0]["services"],
      }),
  });

  const documentCreateRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: "/modules/document-workflow/documents/new",
    validateSearch: (search) => documentCreateSearchSchema.parse(search),
    component: () => createElement(DocumentCreatePage, { services }),
  });

  const forgotPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/forgot-password",
    component: () => createElement(ForgotPasswordPage, { services }),
  });
  const resetPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/reset-password",
    component: () => createElement(ResetPasswordPage, { services }),
  });
  const emailVerificationRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/email/verify/$id/$hash",
    component: () => createElement(EmailVerificationPage, { services }),
  });

  const routeTree = rootRoute.addChildren([
    loginRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    emailVerificationRoute,
    unauthorizedRoute,
    ...errorRoutes,
    authenticatedRoute.addChildren([
      overviewRoute,
      notificationsRoute,
      mailAdminRoute,
      counterpartiesRoute,
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/modules/document-workflow/approvals",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "document-workflow.approvals.view"),
        component: () => createElement(ApprovalsOverviewPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/modules/document-workflow/approvals/tasks",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "document-workflow.approvals.view"),
        component: () => createElement(ApprovalTasksPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/modules/document-workflow/approvals/templates",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "document-workflow.approvals.view"),
        component: () => createElement(ApprovalTemplatesPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/modules/document-workflow/approval-templates",
        beforeLoad: async ({ context }) => {
          await guardedPermission(context, "document-workflow.approvals.view");
          // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirects are control-flow values.
          throw redirect({ to: "/modules/document-workflow/approvals/templates" });
        },
      }),
      mailboxesRoute,
      documentCreateRoute,
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/site/slides",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "site.settings.manage"),
        component: () =>
          createElement(SiteSlidesPage, {
            services: services.siteContent!,
          }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/users",
        validateSearch: validateUsersSearch,
        beforeLoad: async ({ context, search }) => {
          // Карточку сотрудника открывали ссылкой /administration/users?user=…:
          // такие ссылки живут в уведомлениях и аудите, поэтому ведём их в список.
          if (search.user !== undefined) {
            // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirects are control-flow values.
            throw redirect({
              to: "/administration/users/list",
              search: { user: search.user },
            });
          }
          await requireAnyPermission(context as RouterContext, [
            "users.view",
            "users.hierarchy.view",
          ]);
        },
        component: () => createElement(UsersOverviewPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/users/list",
        validateSearch: validateUsersSearch,
        beforeLoad: ({ context }) => guardedPermission(context, "users.view"),
        component: () => createElement(UsersRoute, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/users/hierarchy",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "users.hierarchy.view"),
        component: () => createElement(UserHierarchyPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/audit",
        validateSearch: validateAuditSearch,
        beforeLoad: ({ context }) => guardedPermission(context, "audit.view"),
        component: () => createElement(AuditLogPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/files",
        validateSearch: validateFilesSearch,
        beforeLoad: ({ context }) => guardedPermission(context, "files.upload"),
        component: () => createElement(FilesRoute, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/modules",
        beforeLoad: ({ context }) => guardedPermission(context, "modules.view"),
        component: () => createElement(ModulesAdminPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/operations",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "modules.configure"),
        component: () => createElement(OperationsPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/site",
        beforeLoad: ({ context }) => guardedPermission(context, "site.access"),
        component: () => createElement(SiteContentOverviewPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/site/news",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "site.news.view"),
        component: () => createElement(SiteNewsPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/site/categories",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "site.categories.manage"),
        component: () =>
          createElement(SiteCategoriesPage, {
            services: services.siteContent!,
            canManage: true,
          }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/site/pages",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "site.pages.manage"),
        component: () =>
          createElement(SitePagesPage, {
            services: services.siteContent!,
            canManage: true,
          }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/site/settings",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "site.settings.manage"),
        component: () =>
          createElement(SiteSettingsPage, {
            services: services.siteContent!,
            canManage: true,
          }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/roles",
        beforeLoad: ({ context }) => guardedPermission(context, "roles.view"),
        component: () => createElement(RoleManagementPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/scopes/$scopeId/departments/$departmentId",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "organization.view"),
        component: DepartmentDetailRoute,
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/scopes",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "organization.view"),
        component: () => createElement(ScopeManagementPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/administration/scopes/$scopeId",
        beforeLoad: ({ context }) =>
          guardedPermission(context, "organization.view"),
        component: ScopeDetailRoute,
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/account/security",
        component: () => createElement(SecuritySettingsPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/account/sessions",
        component: () => createElement(SessionsPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/account",
        component: () => createElement(AccountPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/account/substitution",
        beforeLoad: ({ context }) =>
          requireAnyPermission(context as RouterContext, [
            "document-workflow.substitutions.manage",
            "document-workflow.substitutions.manage.any",
          ]),
        component: () => createElement(MySubstitutionPage, { services }),
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/about",
        component: AboutPage,
      }),
      createRoute({
        getParentRoute: () => authenticatedRoute,
        path: "/guide",
        // Доступ тот же, что был у ссылки на Confluence: право просмотра документооборота.
        beforeLoad: ({ context }) =>
          guardedPermission(context, "document-workflow.view"),
        component: () => createElement(GuidePage, { services }),
      }),
      moduleRoute,
    ]),
  ]);

  return createRouter({
    routeTree,
    context: { queryClient, services },
    history,
    defaultPendingMs: 0,
    defaultPreload: "intent",
  });
}

export type ApplicationRouter = ReturnType<typeof createApplicationRouter>;
